import React, { useReducer, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';
import './caseForm.scss';
import { useNavigate } from 'react-router-dom';

const CaseForm = () => {
  const navigate = useNavigate();
  const caseId = parseInt(window.location.pathname.split('/')[2]) || null;
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const reducer = (state, action) => {
    switch (action.type) {
      case 'UPDATE_FIELD':
        return {
          ...state,
          [action.field]: action.value,
        };
      default:
        return state;
    }
  };

  const initialCase = {
    name: 'untitled',
    description: 'no description',
    targets: [],
  };

  const [_case, dispatch] = useReducer(reducer, initialCase);

  // Fetch existing case data if caseId is provided (editing mode)
  const { isLoading: isCaseLoading } = useQuery(
    ['caseTargets', caseId],
    () => api.get(`/api/osint/case/${caseId}/`).then((res) => res.data),
    {
      enabled: !!caseId,
      onSuccess: (data) => {
        dispatch({ type: 'UPDATE_FIELD', field: 'name', value: data.name || '' });
        dispatch({ type: 'UPDATE_FIELD', field: 'description', value: data.description || '' });
        dispatch({ type: 'UPDATE_FIELD', field: 'targets', value: data.targets || [] });
      },
    }
  );

  // Fetch all available targets
  const { data: availableTargets = [], isLoading: isTargetsLoading } = useQuery(
    'availableTargets',
    () => api.get('/api/osint/list_targets/').then((res) => res.data)
  );

  // Handle input changes
  const handleChange = (e) => {
    dispatch({ type: 'UPDATE_FIELD', field: e.target.name, value: e.target.value });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
  
    try {
      // Prepare the case data to send to the API
      const caseData = {
        name: _case.name.trim(),
        description: _case.description.trim(),
        targets: caseTargets, // Use the array of target IDs
      };
  
      if (!caseData.name) {
        setError('Case name is required.');
        return;
      }
  
      if (caseId) {
        // Update existing case
        await api.put(`/api/osint/case/${caseId}/`, caseData);
        navigate(`/case/${caseId}`);
      } else {
        // Create a new case
        const response = await api.post('/api/osint/case/', caseData);
        const newCaseId = response.data.id;
        navigate(`/case/${newCaseId}`);
      }
    } catch (err) {
      // Handle errors gracefully
      if (err.response && err.response.data && err.response.data.detail) {
        setError(`Failed to save case: ${err.response.data.detail}`);
      } else {
        setError('Failed to save case.');
      }
      console.error(err);
    }
  };


  // State for selected targets in left and right panels
  const [selectedLeft, setSelectedLeft] = useState([]);
  const [selectedRight, setSelectedRight] = useState(_case.targets);

  // State for target IDs in the case (right panel)
  const [caseTargets, setCaseTargets] = useState(_case.targets || []);

  // Functions to move targets between panels
  const moveToRight = () => {
    const updatedCaseTargets = Array.from(new Set([...caseTargets, ...selectedLeft])); // Avoid duplicates
    setCaseTargets(updatedCaseTargets);
    setSelectedLeft([]);
    setSelectedRight([]);
    dispatch({ type: 'UPDATE_FIELD', field: 'targets', value: updatedCaseTargets });
  };

  const moveToLeft = () => {
    const updatedCaseTargets = caseTargets.filter((id) => !selectedRight.includes(id));
    setCaseTargets(updatedCaseTargets);
    setSelectedLeft([]);
    setSelectedRight([]);
    dispatch({ type: 'UPDATE_FIELD', field: 'targets', value: updatedCaseTargets });
  };

  const moveAllToRight = () => {
    const allAvailableIds = availableTargets.map((target) => target.id);
    const updatedCaseTargets = Array.from(new Set([...caseTargets, ...allAvailableIds]));
    setCaseTargets(updatedCaseTargets);
    setSelectedLeft([]);
    setSelectedRight([]);
    dispatch({ type: 'UPDATE_FIELD', field: 'targets', value: updatedCaseTargets });
  };
  
  const moveAllToLeft = () => {
    setCaseTargets([]);
    setSelectedLeft([]);
    setSelectedRight([]);
    dispatch({ type: 'UPDATE_FIELD', field: 'targets', value: [] });
  };

  // Update selectedRight when _case.targets changes
useEffect(() => {
  if (_case.targets) {
    setCaseTargets(_case.targets || []);
    setSelectedRight(_case.targets.map((target) => target.id));
  }
}, [_case.targets]);

  if (isCaseLoading || isTargetsLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="case">
      <div className="images">
        <img
          src="/images/case_alt3.png"
          alt="Cover"
          className="cover"
        />
        <img
          src={_case.img? _case.img : "/images/case_default_cover.png"}
          alt="Case"
          className="casePic"
        />
      </div>
      <div className="caseContainer">
        <div className="cInfo">
          <div className="center">
            <span className='caseName'>{_case.name}</span>
            <div className="info">
              <div className="item">
                <span>Description:</span>
                <span className='caseDescription'>{_case.description}</span>
              </div>
            </div>
          </div>
        </div>
    <div className="caseForm">
    <form onSubmit={handleSubmit}>
      <div className='formGroup'>
        <label htmlFor="name">Name:</label>
        <input
          type="text"
          id="name"
          name="name"
          value={_case.name}
          onChange={handleChange}
          required
        />
      </div>
      <div  className='formGroup'>
        <label htmlFor="description">Description:</label>
        <textarea
          id="description"
          name="description"
          value={_case.description}
          onChange={handleChange}
          required
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {/* Left Panel */}
        <div className='formGroup'>
        <label>Available Targets:</label>
        <select
          multiple
          value={selectedLeft}
          onChange={(e) =>
            setSelectedLeft(Array.from(e.target.selectedOptions, (option) => parseInt(option.value)))
          }
          style={{ width: '200px', height: '200px' }}
        >
          {availableTargets
            .filter((target) => !caseTargets.includes(target.id))
            .map((target) => (
              <option key={target.id} value={target.id}>
                {target.name}
              </option>
            ))}
        </select>
        </div>

        {/* Buttons */}
        <div style={{ margin: '0 20px', display: 'flex', flexDirection: 'column' }}>
          <button type="button" onClick={moveAllToRight}>{'>>'}</button>
          <button type="button" onClick={moveToRight}>{'>'}</button>
          <button type="button" onClick={moveToLeft}>{'<'}</button>
          <button type="button" onClick={moveAllToLeft}>{'<<'}</button>
        </div>

        {/* Right Panel */}
        <div className='formGroup'>
        <label>Selected Targets:</label>
        <select
          multiple
          value={selectedRight}
          onChange={(e) =>
            setSelectedRight(Array.from(e.target.selectedOptions, (option) => parseInt(option.value)))
          }
          style={{ width: '200px', height: '200px' }}
        >
          {caseTargets
            .map((id) => availableTargets.find((target) => target.id === id))
            .filter(Boolean) // Exclude undefined if any IDs don't match
            .map((target) => (
              <option key={target.id} value={target.id}>
                {target.name}
              </option>
            ))}
        </select>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
        <button type="button" onClick={() => navigate(`/case/${caseId}`)}>Cancel</button>
        <button type="submit">Save</button>
      </div>
    </form>
    </div>
    </div>
    </div>
  );
};

export default CaseForm;